import React, { useContext, useEffect, useState } from "react";
import axios from "../../utils/Api";
import "./Home.css";
import { Switch, FormControlLabel, Avatar } from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import HomeFallback from "./components/HomeFallback";
import BadgeComponent from "../../composant/PageProfil/BadgeComponent";
import Role from "../../policies/Role";

export default function Home() {
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [feedEnabled, setFeedEnabled] = useState(false);

  const { user, loading: loadingAuth } = useContext(AuthContext);

  const isAdmin =
    user?.role === Role.Admin || user?.role === Role.AdminContact;

  const isTeacher = user?.role === Role.Teacher;
  const isStudent = !isAdmin && !isTeacher;

  // Seulement le prof voit le switch
  const canToggleFeed = isTeacher;

  useEffect(() => {
    if (loadingAuth) return;

    // Élève : toujours actualité
    if (isStudent) {
      setFeedEnabled(true);
      return;
    }

    // Admin : normalement redirigé vers /admin
    // S'il arrive ici par erreur, on affiche quand même l'actualité.
    if (isAdmin) {
      setFeedEnabled(true);
      return;
    }

    // Prof : tableau de bord par défaut
    if (isTeacher) {
      const userId = user?.id || user?.username || "default";
      const savedValue = localStorage.getItem(`feedEnabled_${userId}`);

      if (savedValue === null) {
        setFeedEnabled(false);
      } else {
        setFeedEnabled(savedValue === "true");
      }
    }
  }, [loadingAuth, user, isStudent, isAdmin, isTeacher]);

  useEffect(() => {
    if (loadingAuth) return;

    if (!feedEnabled) {
      setNotifications([]);
      setLoadingNotifications(false);
      return;
    }

    setLoadingNotifications(true);

    axios
      .get("/notification")
      .then((res) => {
        const filteredNotifications = (res.data || []).filter(
          (notif) => notif.type !== "system_reset"
        );

        setNotifications(filteredNotifications);
      })
      .catch(() => {
        setNotifications([]);
      })
      .finally(() => {
        setLoadingNotifications(false);
      });
  }, [feedEnabled, loadingAuth]);

  const handleDelete = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleToggleFeed = (event) => {
    const newValue = event.target.checked;

    setFeedEnabled(newValue);

    const userId = user?.id || user?.username || "default";
    localStorage.setItem(`feedEnabled_${userId}`, String(newValue));
  };

  if (loadingAuth) {
    return <div className="home-loading">Chargement...</div>;
  }

  return (
    <div className="home home-no-sidebar">
      <main className="home-main">
        <div className="home-content">
          <div className="home-header">
            <div>
              <h2 className="home-title">
                {feedEnabled ? "Fil d'actualité" : "Tableau de bord"}
              </h2>

              <p className="home-subtitle">
                Suivez les activités et performances en temps réel
              </p>
            </div>

            {canToggleFeed && (
              <FormControlLabel
                control={
                  <Switch
                    checked={feedEnabled}
                    onChange={handleToggleFeed}
                  />
                }
                label={feedEnabled ? "Fil activé" : "Fil désactivé"}
              />
            )}
          </div>

          {!feedEnabled && canToggleFeed ? (
            <HomeFallback user={user} />
          ) : loadingNotifications ? (
            <div className="home-loading">Chargement...</div>
          ) : notifications.length === 0 ? (
            <div className="home-empty">Aucune actualité pour le moment.</div>
          ) : (
            <div className="feed">
              {notifications.map((notif) => (
                <NotificationCard
                  key={notif.id}
                  notif={notif}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function NotificationCard({ notif, onDelete }) {
  const getMessage = () => {
    const name = notif.user
      ? `${notif.user.first_name} ${notif.user.last_name}`
      : "Inconnu";

    switch (notif.type) {
      case "badge_created":
        return `${name} a créé un badge`;

      case "badge_earned":
        return `${name} a gagné un badge`;

      case "ranking":
        return `${name} est maintenant ${notif.rank}${
          notif.rank === 1 ? "ère" : "ème"
        } position`;

      default:
        return "Notification";
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);

    if (Number.isNaN(d.getTime())) {
      return "";
    }

    return d.toLocaleDateString("fr-CA");
  };

  const renderUserAvatar = (size = 64) => {
    return (
      <Avatar
        src={notif.user?.avatarImagePath}
        alt={notif.user?.first_name || "user"}
        sx={{
          width: size,
          height: size,
          border: "2px solid #1976d2",
          flexShrink: 0,
        }}
      >
        {notif.user?.first_name?.charAt(0) || "?"}
      </Avatar>
    );
  };

  const renderContent = () => {
    switch (notif.type) {
      case "badge_created":
        return (
          <div className="card-body card-center">
            <div className="feed-badge-box">
              {notif.badge && <BadgeComponent badge={notif.badge} />}
            </div>
          </div>
        );

      case "badge_earned":
        return (
          <div className="card-body card-earned">
            <div className="feed-avatar-box">{renderUserAvatar(64)}</div>

            <div className="feed-separator">•</div>

            <div className="feed-badge-box">
              {notif.badge && <BadgeComponent badge={notif.badge} />}
            </div>
          </div>
        );

      case "ranking":
        return (
          <div className="card-body card-center">
            <div className="card-avatar-wrapper card-avatar-large">
              {renderUserAvatar(96)}

              <div className={`card-rank card-rank-${notif.rank}`}>
                #{notif.rank}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-info">
          <div>
            <p className="card-title">{getMessage()}</p>
            <p className="card-date">{formatDate(notif.created_at)}</p>
          </div>
        </div>

        <button
          className="card-close"
          type="button"
          onClick={() => onDelete(notif.id)}
        >
          ✕
        </button>
      </div>

      {renderContent()}
    </div>
  );
}